import { useState } from 'react';

import PostLogImportRecordsAPI from 'api/postLogImportRecordsAPI';

import UploadButton from 'components/base/UploadButton';
import FileUploadControl from 'components/UIComponents/input/FileUploadControl';

import { usePopup } from 'context/GlobalPopupContext';
import { useToast } from 'context/ToastContext';

import { PopupType, ToastType } from 'constants/enums/PopupType';
import { postLogImportRecordsStatuses } from 'constants/spots';

const HTTP_BAD_REQUEST = 400;

const UploadPostLogsForm = () => {
  const { openPopup } = usePopup();
  const { onToastOpen } = useToast();
  const [loading, setLoading] = useState<boolean>(false);

  const isOk = (response: any) =>
    response?.data?.status === postLogImportRecordsStatuses.awaitingConfirmation ||
    response?.data?.status === postLogImportRecordsStatuses.inProgress;

  const handleUpload = async (file: File) => {
    setLoading(true);
    const response = await PostLogImportRecordsAPI.create({ file_name: file.name }, file);
    setLoading(false);

    if (!isOk(response)) {
      onToastOpen(
        `post-log-upload-error-${file.name}`,
        'Failed to upload post log file.',
        ToastType.error
      );
      return;
    }

    const status = response?.data?.status;
    if (status === postLogImportRecordsStatuses.inProgress) {
      onToastOpen(
        `post-log-upload-started-${file.name}`,
        'Upload started, processing now',
        ToastType.info
      );
      return;
    }
    if (status === postLogImportRecordsStatuses.awaitingConfirmation) {
      const id = response.data.id;
      const invalid = response.data.rows_invalid;
      openPopup({
        type: PopupType.warning,
        title: 'Post Logs Import Issue',
        content:
          `${invalid} row${invalid === 1 ? '' : 's'} couldn't be processed due to errors. ` +
          'You can proceed with the valid rows, or cancel to fix the file and try again.',
        cancelText: 'Cancel Import',
        closeCallback: async () => {
          const cancelRes = await PostLogImportRecordsAPI.cancel(id);
          if (cancelRes?.status >= HTTP_BAD_REQUEST) {
            onToastOpen(
              `post-log-cancel-error-${id}`,
              'Failed to cancel import.',
              ToastType.error
            );
          }
        },
        submitText: 'Import Valid Rows',
        submitCallback: async () => {
          const confirmRes = await PostLogImportRecordsAPI.confirm(id);
          if (confirmRes?.status >= HTTP_BAD_REQUEST) {
            onToastOpen(
              `post-log-confirm-error-${id}`,
              'Failed to confirm import.',
              ToastType.error
            );
          }
        },
      });
    }
  };

  return (
    <FileUploadControl
      inputProps={{ accept: 'text/csv,.csv', 'data-testid': 'upload-post-logs-input' }}
      maxFileSize={10}
      ActionButton={({ onClick }: { onClick: () => void }) => (
        <UploadButton title="Upload post logs" loading={loading} onClick={onClick} />
      )}
      onProcessFile={handleUpload}
    />
  );
};

export default UploadPostLogsForm;
